<section class="cp-card" aria-labelledby="cc-title">
  <div class="flex items-start justify-between gap-3">
    <div>
      <h2 id="cc-title" class="cp-card__title">Saldo a favor</h2>
      <p class="mt-0.5 text-theme-xs text-gray-500 dark:text-gray-400">Se puede usar para pagar sus próximas citas.</p>
    </div>
  </div>

  @if (isLoading() && !recent().length) {
  <div class="mt-4 h-10 w-40 animate-pulse rounded-lg bg-gray-100 dark:bg-white/[0.05]"></div>
  } @else if (loadFailed()) {
  <p class="mt-4 text-theme-sm text-gray-600 dark:text-gray-300">
    No pudimos cargar el saldo.
    <button type="button" class="cc-link" (click)="load()">Reintentar</button>
  </p>
  } @else {
  <p class="cc-balance mt-4" [class.cc-balance--negative]="balance() < 0">{{ formatMoney(balance()) }}</p>
  @if (balance() < 0) {
  <p class="mt-1 text-theme-xs text-error-600 dark:text-error-400">
    El saldo quedó en negativo por pagos registrados antes de una corrección. Revisa los movimientos.
  </p>
  }

  <div class="mt-4 grid grid-cols-2 gap-2">
    <button type="button" class="cc-btn cc-btn--primary" (click)="openAdjust('add')">
      <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
      Agregar saldo
    </button>
    <button type="button" class="cc-btn cc-btn--ghost" (click)="openAdjust('deduct')" [disabled]="balance() <= 0">
      <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14" /></svg>
      Descontar
    </button>
  </div>

  @if (recent().length) {
  <ul class="mt-5 divide-y divide-gray-100 dark:divide-gray-800" aria-label="Últimos movimientos">
    @for (m of recent(); track m.movementId) {
    <li class="flex items-center justify-between gap-3 py-2.5">
      <div class="min-w-0">
        <p class="truncate text-theme-sm text-gray-800 dark:text-white/90">{{ m.description }}</p>
        <p class="text-theme-xs text-gray-500 dark:text-gray-400">{{ toDate(m.createdAt) | date: 'd MMM y' }}</p>
      </div>
      <span class="cc-amount" [class.cc-amount--in]="m.type === 'credit'">
        {{ m.type === 'credit' ? '+' : '−' }}{{ formatMoney(m.amount) }}
      </span>
    </li>
    }
  </ul>
  @if (totalCount() > recent().length) {
  <button type="button" class="cc-link mt-2" (click)="openHistory()">Ver los {{ totalCount() }} movimientos</button>
  }
  } @else {
  <p class="mt-5 text-theme-sm text-gray-500 dark:text-gray-400">
    Aún no tiene movimientos. Agrega saldo cuando el cliente pague por adelantado o le debas un reembolso.
  </p>
  }
  }
</section>

<!-- Add / deduct dialog -->
<dialog #adjustDialog class="cp-dialog" aria-labelledby="cc-adjust-title" (click)="onDialogClick($event, adjustDialog)">
  <form class="cp-dialog__body" (ngSubmit)="submitAdjust()" novalidate>
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 id="cc-adjust-title" class="text-lg font-semibold text-gray-900 dark:text-white">
          {{ mode() === 'add' ? 'Agregar saldo' : 'Descontar saldo' }}
        </h2>
        <p class="mt-0.5 text-theme-sm text-gray-500 dark:text-gray-400">
          Saldo actual: {{ formatMoney(balance()) }}
        </p>
      </div>
      <button type="button" class="cc-close" (click)="closeAdjust()" aria-label="Cerrar">
        <svg class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
    </div>

    <div class="cc-segment mt-5" role="radiogroup" aria-label="Tipo de movimiento">
      <button type="button" role="radio" [attr.aria-checked]="mode() === 'add'" (click)="mode.set('add')">Agregar</button>
      <button type="button" role="radio" [attr.aria-checked]="mode() === 'deduct'" (click)="mode.set('deduct')"
        [disabled]="balance() <= 0">Descontar</button>
    </div>

    <label for="cc-amount" class="cc-label mt-5">Monto</label>
    <input id="cc-amount" type="text" inputmode="numeric" autocomplete="off" class="cc-input cc-input--amount"
      [class.cc-input--error]="submitted() && amountError()"
      [value]="amount() ? formatMoney(amount()) : ''" (input)="onAmountInput($any($event.target).value)"
      placeholder="$ 0" [attr.aria-invalid]="submitted() && !!amountError()" aria-describedby="cc-amount-msg" />
    <div class="mt-2 flex flex-wrap gap-2">
      @for (q of quickAmounts; track q) {
      <button type="button" class="cc-chip" (click)="amount.set(q)" [attr.aria-pressed]="amount() === q">
        {{ formatMoney(q) }}
      </button>
      }
      @if (mode() === 'deduct' && balance() > 0) {
      <button type="button" class="cc-chip" (click)="amount.set(balance())" [attr.aria-pressed]="amount() === balance()">
        Todo el saldo
      </button>
      }
    </div>
    @if (submitted() && amountError()) {
    <p id="cc-amount-msg" class="mt-2 text-theme-xs text-error-600 dark:text-error-400">{{ amountError() }}</p>
    }

    <label for="cc-reason" class="cc-label mt-5">
      Motivo @if (mode() === 'add') { <span class="font-normal text-gray-400">opcional</span> }
    </label>
    <textarea id="cc-reason" rows="2" maxlength="500" class="cc-input cc-textarea" name="reason"
      [class.cc-input--error]="submitted() && reasonError()"
      [ngModel]="reason()" (ngModelChange)="reason.set($event)"
      [placeholder]="mode() === 'add' ? 'Ej: pagó por adelantado el paquete de 4 sesiones' : 'Ej: se le devolvió en efectivo'"></textarea>
    @if (submitted() && reasonError()) {
    <p class="mt-2 text-theme-xs text-error-600 dark:text-error-400">{{ reasonError() }}</p>
    }

    <div class="cc-summary mt-5">
      <span>Nuevo saldo</span>
      <strong [class.text-error-600]="newBalance() < 0">{{ formatMoney(newBalance()) }}</strong>
    </div>

    <div class="mt-5 flex justify-end gap-2">
      <button type="button" class="cc-btn cc-btn--ghost" (click)="closeAdjust()">Cancelar</button>
      <button type="submit" class="cc-btn cc-btn--primary" [disabled]="isSaving()">
        @if (isSaving()) { Guardando… } @else { {{ mode() === 'add' ? 'Agregar saldo' : 'Descontar saldo' }} }
      </button>
    </div>
  </form>
</dialog>

<!-- Full history dialog -->
<dialog #historyDialog class="cp-dialog cp-dialog--wide" aria-labelledby="cc-history-title"
  (click)="onDialogClick($event, historyDialog)">
  <div class="cp-dialog__body">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h2 id="cc-history-title" class="text-lg font-semibold text-gray-900 dark:text-white">Movimientos de saldo</h2>
        <p class="mt-0.5 text-theme-sm text-gray-500 dark:text-gray-400">{{ clientName }}</p>
      </div>
      <button type="button" class="cc-close" (click)="closeHistory()" aria-label="Cerrar">
        <svg class="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
      </button>
    </div>

    <dl class="mt-5 grid grid-cols-3 gap-3 text-theme-sm">
      <div class="cc-stat"><dt>Saldo</dt><dd>{{ formatMoney(balance()) }}</dd></div>
      <div class="cc-stat"><dt>Agregado</dt><dd class="text-success-600 dark:text-success-400">{{ formatMoney(totalCredited()) }}</dd></div>
      <div class="cc-stat"><dt>Usado</dt><dd>{{ formatMoney(totalUsed()) }}</dd></div>
    </dl>

    <ul class="mt-4 max-h-[55vh] divide-y divide-gray-100 overflow-y-auto dark:divide-gray-800">
      @for (m of history(); track m.movementId) {
      <li class="flex items-center justify-between gap-3 py-3">
        <div class="min-w-0">
          <p class="text-theme-sm text-gray-800 dark:text-white/90">{{ m.description }}</p>
          <p class="text-theme-xs text-gray-500 dark:text-gray-400">{{ toDate(m.createdAt) | date: "d MMM y, h:mm a" }}</p>
        </div>
        <span class="cc-amount" [class.cc-amount--in]="m.type === 'credit'">
          {{ m.type === 'credit' ? '+' : '−' }}{{ formatMoney(m.amount) }}
        </span>
      </li>
      }
    </ul>
    @if (historyLoading()) {
    <p class="py-3 text-center text-theme-sm text-gray-500">Cargando…</p>
    } @else if (hasMoreHistory()) {
    <button type="button" class="cc-btn cc-btn--ghost mt-3 w-full" (click)="loadMoreHistory()">Mostrar más</button>
    }
  </div>
</dialog>
